import React, { FC, memo, useEffect, useMemo } from 'react';
import CollapsedSection from '@components/CollapsedSection';
import {
  useIsLayerManuallyCollapsed,
  useIsSearchFiltering,
  useListSearchActions,
  useSearchNeedle,
} from '@components/MainStage/listSearch.store';
import {
  LayerSubLayers,
  LayerTypes,
  Modes,
  PhysicalSubLayerTypes,
  ReadableLayerTitles,
} from '@constants/canvas/layers';
import { PhysicalLayerInRiskModeLabel } from '@constants/canvas/riskManagement';
import { useMenuNodes } from '@context/MenuNodesProvider';
import { useProject } from '@context/Project/ProjectProvider';
import useToggle from '@hooks/useToggle';
import { matchesSearch } from '@utils/searchHighlight';

import SubLayer from './SubLayer';

type Props = {
  layerId: LayerTypes;
};

const LayerMenu: FC<Props> = ({ layerId }) => {
  const {
    toolbox: { mode },
  } = useProject();

  const [isExpanded, { toggle, off }] = useToggle(false);

  const isFiltering = useIsSearchFiltering();
  const needle = useSearchNeedle();
  const isManuallyCollapsed = useIsLayerManuallyCollapsed(layerId);
  const { toggleManualCollapse } = useListSearchActions();

  useEffect(() => {
    off();
  }, [mode]);

  const { nodes } = useMenuNodes();

  const layerNodes = useMemo(
    () =>
      nodes.filter((node) =>
        LayerSubLayers[layerId].includes(
          node.parentNode as PhysicalSubLayerTypes,
        ),
      ),
    [layerId, nodes],
  );

  const matchCount = useMemo(
    () =>
      isFiltering
        ? layerNodes.filter((node) =>
            matchesSearch(node.data.dto.label, needle),
          ).length
        : layerNodes.length,
    [isFiltering, needle, layerNodes],
  );

  const sectionExpanded = isFiltering ? !isManuallyCollapsed : isExpanded;

  const handleToggle = () => {
    if (isFiltering) {
      toggleManualCollapse(layerId);

      return;
    }

    toggle();
  };

  let riskModeLayerMenuLabel = '';

  if (mode === Modes.RiskManagement) {
    switch (layerId) {
      case LayerTypes.Physical:
        riskModeLayerMenuLabel = PhysicalLayerInRiskModeLabel;
        break;
      case LayerTypes.Initiatives:
        riskModeLayerMenuLabel =
          ReadableLayerTitles[LayerTypes.RemediationAction];
        break;
      default:
        riskModeLayerMenuLabel = ReadableLayerTitles[layerId];
        break;
    }
  }

  if (isFiltering && matchCount === 0) return null;

  return (
    <div>
      <CollapsedSection
        controls={{ isExpanded: sectionExpanded, toggle: handleToggle }}
        title={`${
          mode === Modes.RiskManagement
            ? riskModeLayerMenuLabel
            : `${ReadableLayerTitles[layerId]}`
        } (${matchCount})`}
      >
        {LayerSubLayers[layerId].map((subLayerId) => (
          <SubLayer
            key={subLayerId}
            subLayerId={subLayerId}
            layerId={layerId}
          />
        ))}
      </CollapsedSection>
    </div>
  );
};

export default memo(LayerMenu);
